import {useEffect, useState} from 'react';

/**
 * useInternetStatus - A hook that checks if the internet is connected or not.
 *
 * @returns {Object} - An object containing:
 *  - isOnline {boolean}: Whether the internet is connected or not.
 *  - isInternetChecking {boolean}: Whether the internet status is currently being checked.
 *  - internetCheckingMsg {string}: A message indicating the current status of the internet check.
 *
 * @example
 * import React from 'react';
 * import useInternetStatus from './useInternetStatus';
 *
 * const InternetStatusComponent = () => {
 *   const { isOnline, isInternetChecking, internetCheckingMsg } = useInternetStatus();
 *
 *   return (
 *     <div>
 *       {isInternetChecking ? (
 *         <p>{internetCheckingMsg}</p>
 *       ) : (
 *         <p>{isOnline ? 'You are online' : 'You are offline'}</p>
 *       )}
 *     </div>
 *   );
 * };
 *
 * export default InternetStatusComponent;
 */
const useInternetStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [isInternetChecking, setIsInternetChecking] = useState(false);
    const [internetCheckingMsg, setInternetCheckingMsg] = useState('Checking internet status...');

    useEffect(() => {
        const updateOnlineStatus = () => {
            setIsOnline(navigator.onLine);
            setIsInternetChecking(false);
            setInternetCheckingMsg(navigator.onLine ? 'You are online.' : 'You are offline!');
        };

        setIsInternetChecking(true);
        setInternetCheckingMsg('Checking internet status...');
        updateOnlineStatus();

        window.addEventListener('online', updateOnlineStatus);
        window.addEventListener('offline', updateOnlineStatus);

        return () => {
            window.removeEventListener('online', updateOnlineStatus);
            window.removeEventListener('offline', updateOnlineStatus);
        };
    }, []);

    return {
        isOnline,
        isInternetChecking,
        internetCheckingMsg,
    };
};

export default useInternetStatus;
